import { useState, useEffect, useRef } from 'react';

const KEY = 'vbot_lead_chat_v1';
const load = (id) => { try { return (JSON.parse(localStorage.getItem(KEY) || '{}')[id]) || []; } catch (e) { return []; } };
const save = (id, msgs) => {
  try { const all = JSON.parse(localStorage.getItem(KEY) || '{}'); all[id] = msgs.slice(-200); localStorage.setItem(KEY, JSON.stringify(all)); } catch (e) {}
};
const timeOf = (ts) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const dayOf = (ts) => new Date(ts).toDateString();
const dayLabel = (ts) => {
  const d = new Date(ts), t = new Date();
  if (d.toDateString() === t.toDateString()) return 'Today';
  const y = new Date(); y.setDate(t.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};
const QUICK = [
  'Hi {name}, thanks for your enquiry. How can I help you?',
  'Sharing the details now. Please check and let me know.',
  'Are you still interested? Happy to help.',
];

const Ticks = ({ status }) => {
  const base = { fontSize: 11, marginLeft: 4, letterSpacing: -2 };
  if (status === 'read') return <span style={{ ...base, color: '#34b7f1' }}>✓✓</span>;
  if (status === 'delivered') return <span style={{ ...base, color: '#9aa0a6' }}>✓✓</span>;
  return <span style={{ ...base, color: '#9aa0a6' }}>✓</span>;
};

export function LeadChat({ lead, waUrl }) {
  const [msgs, setMsgs] = useState(() => load(lead.id));
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { setMsgs(load(lead.id)); }, [lead.id]);
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: 'end' }); }, [msgs.length]);

  const lastIn = lead.last_customer_reply_at ? new Date(lead.last_customer_reply_at).getTime() : null;
  const windowOpen = lastIn && Date.now() - lastIn < 24 * 3600 * 1000;
  const hoursLeft = windowOpen ? Math.max(1, Math.round((24 * 3600 * 1000 - (Date.now() - lastIn)) / 3600000)) : 0;

  const send = () => {
    const body = text.trim();
    if (!body && !file) return;
    const next = [...msgs, { id: Date.now(), dir: 'out', text: body, file, ts: Date.now(), status: 'sent' }];
    setMsgs(next); save(lead.id, next);
    setText(''); setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const pickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) setFile({ name: f.name, type: f.type, size: f.size });
  };
  const sizeOf = (b) => (b > 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB');
  const iconOf = (t) => (t || '').startsWith('image') ? '🖼️' : (t || '').startsWith('video') ? '🎬' : '📄';

  const banner = windowOpen
    ? { bg: '#e8f5e9', color: '#2e7d32', text: `24-hour window open (~${hoursLeft}h left). You can send a normal message.` }
    : lastIn
      ? { bg: '#fff8e1', color: '#e65100', text: '24-hour window closed. Only an approved template can be sent now.' }
      : { bg: '#f3f4f6', color: '#6b7280', text: 'Customer has not replied yet. Use an approved template to start the chat.' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 230px)', minHeight: 380 }}>
      <div style={{ background: banner.bg, color: banner.color, fontSize: 11, fontWeight: 700, padding: '8px 14px' }}>{banner.text}</div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 6px', background: '#efeae2' }}>
        {msgs.length === 0 && (
          <div style={{ textAlign: 'center', color: '#8a8f94', fontSize: 12, padding: '40px 10px' }}>
            <div style={{ fontSize: 34, marginBottom: 6 }}>💬</div>
            No messages yet. Send your first message below.
          </div>
        )}
        {msgs.map((m, i) => {
          const out = m.dir === 'out';
          const newDay = i === 0 || dayOf(msgs[i - 1].ts) !== dayOf(m.ts);
          return (
            <div key={m.id}>
              {newDay && (
                <div style={{ textAlign: 'center', margin: '8px 0' }}>
                  <span style={{ background: '#fff', color: '#667781', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8 }}>{dayLabel(m.ts)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                <div style={{ maxWidth: '78%', background: out ? '#d9fdd3' : '#fff', borderRadius: 10, padding: '7px 10px 5px', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>
                  {m.file && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,.05)', borderRadius: 8, padding: '6px 8px', marginBottom: m.text ? 6 : 2 }}>
                      <span style={{ fontSize: 20 }}>{iconOf(m.file.type)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>{m.file.name}</div>
                        <div style={{ fontSize: 10, color: '#667781' }}>{sizeOf(m.file.size)}</div>
                      </div>
                    </div>
                  )}
                  {m.text && <div style={{ fontSize: 13, color: '#111b21', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>}
                  <div style={{ textAlign: 'right', fontSize: 10, color: '#667781', marginTop: 2 }}>
                    {timeOf(m.ts)}{out && <Ticks status={m.status} />}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div style={{ background: '#fff', borderTop: '1px solid #eceef1', padding: '8px 12px 10px' }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6 }}>
          {QUICK.map(q => (
            <button key={q} onClick={() => setText(q.replace('{name}', lead.name || 'there'))}
              style={{ flexShrink: 0, border: '1px solid #cdeedd', background: '#f0faf5', color: '#075e54', borderRadius: 16, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              {(() => { const t = q.replace('{name}', lead.name || 'there'); return t.length > 34 ? t.slice(0, 34) + '…' : t; })()}
            </button>
          ))}
        </div>
        {file && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3f4f6', borderRadius: 8, padding: '4px 8px', fontSize: 11, marginBottom: 6 }}>
            {iconOf(file.type)} {file.name}
            <span onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }} style={{ cursor: 'pointer', color: '#c62828', fontWeight: 800 }}>×</span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <input ref={fileRef} type="file" accept="image/*,video/*,.pdf,.doc,.docx" onChange={pickFile} style={{ display: 'none' }} />
          <button onClick={() => fileRef.current && fileRef.current.click()} title="Attach file / brochure / video"
            style={{ width: 38, height: 38, borderRadius: '50%', border: '1px solid #e5e7eb', background: '#fafafa', cursor: 'pointer', fontSize: 17, flexShrink: 0 }}>📎</button>
          <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={onKey} rows={1} placeholder="Type a message"
            style={{ flex: 1, resize: 'none', maxHeight: 90, minHeight: 38, padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 20, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
          <button onClick={send} disabled={!text.trim() && !file} title="Send"
            style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', background: '#25d366', color: '#fff', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>➤</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8 }}>
          <span style={{ fontSize: 10, color: '#a0a4a8' }}>Preview mode: saved in this browser only. Live WhatsApp sync comes with backend.</span>
          <button onClick={() => window.open(waUrl, '_blank')} style={{ border: 'none', background: 'none', color: '#075e54', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>Open in WhatsApp ↗</button>
        </div>
      </div>
    </div>
  );
}
